/**
 * Case Conversion Utilities
 * Converts between camelCase and snake_case for API requests/responses
 */

/**
 * Convert camelCase to snake_case
 * Examples:
 * - userName -> user_name
 * - streetAddress -> street_address
 * - isCoverImage -> is_cover_image
 *
 * @param obj - Object to convert
 * @returns Object with snake_case keys
 */
export function camelToSnakeCase<T = Record<string, unknown>>(obj: Record<string, unknown>): T {
  if (obj === null || typeof obj !== "object") {
    return obj as T
  }

  if (Array.isArray(obj)) {
    return obj.map(item => camelToSnakeCase(item)) as unknown as T
  }

  const result: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(obj)) {
    // Convert camelCase to snake_case
    const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)

    // Recursively convert nested objects
    result[snakeKey] =
      typeof value === "object" && value !== null
        ? camelToSnakeCase(value as Record<string, unknown>)
        : value
  }

  return result as T
}

/**
 * Convert snake_case to camelCase
 * Examples:
 * - user_name -> userName
 * - street_address -> streetAddress
 * - is_cover_image -> isCoverImage
 *
 * @param obj - Object to convert
 * @returns Object with camelCase keys
 */
export function snakeToCamelCase<T = Record<string, unknown>>(obj: Record<string, unknown>): T {
  if (obj === null || typeof obj !== "object") {
    return obj as T
  }

  if (Array.isArray(obj)) {
    return obj.map(item => snakeToCamelCase(item)) as unknown as T
  }

  const result: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(obj)) {
    // Convert snake_case to camelCase
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())

    // Recursively convert nested objects
    result[camelKey] =
      typeof value === "object" && value !== null
        ? snakeToCamelCase(value as Record<string, unknown>)
        : value
  }

  return result as T
}
